import { FC, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  CommunityAnalyticsProjectSession,
  CommunityAnalyticsSessionStatus,
  useGetCommunityAnalyticsProjectSessionsQuery,
  useGetCommunityAnalyticsProjectSummaryQuery,
  useLazyGetCommunityAnalyticsProjectSessionsReportQuery,
} from '../api/api';

const PAGE_SIZE = 50;

const columns = [
  { label: 'Date', key: 'created_at', align: 'left' },
  { label: 'Organization', key: 'organization_name', align: 'left' },
  { label: 'Participant', key: 'participant_name', align: 'left' },
  { label: 'Duration', key: 'duration_seconds', align: 'right' },
  { label: 'Score', key: 'score', align: 'right' },
  { label: 'Status', key: 'status', align: 'left' },
] as const;

type SessionSortKey = (typeof columns)[number]['key'];

const getAuthorName = (firstName: string, lastName: string, email: string) =>
  `${firstName} ${lastName}`.trim() || email;

const getParticipantName = (session: CommunityAnalyticsProjectSession) =>
  `${session.participant_first_name ?? ''} ${session.participant_last_name ?? ''}`.trim() || '—';

const formatDateBoundary = (date: string, endOfDay: boolean): string =>
  new Date(`${date}T${endOfDay ? '23:59:59.999' : '00:00:00.000'}`).toISOString();

const formatDateTime = (value: string) => {
  const normalizedValue = value.trim();
  const utcValue = /(Z|[+-]\d{2}:\d{2})$/i.test(normalizedValue)
    ? normalizedValue
    : `${normalizedValue.replace(' ', 'T')}Z`;
  const date = new Date(utcValue);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

const formatDuration = (seconds: number) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;
  return [hours, minutes, remainingSeconds].map((part) => String(part).padStart(2, '0')).join(':');
};

const formatStatus = (status: CommunityAnalyticsSessionStatus) => {
  const labels: Record<CommunityAnalyticsSessionStatus, string> = {
    COMPLETED_WITH_REPORT: 'Completed with report',
    ENGAGED_NO_REPORT: 'Engaged, no report',
    VIEWED_NOT_STARTED: 'Viewed, not started',
  };
  return labels[status];
};

export const CommunityAnalyticsProject: FC = () => {
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId: string }>();
  const [page, setPage] = useState(1);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<CommunityAnalyticsSessionStatus | ''>('');
  const [columnKey, setColumnKey] = useState<SessionSortKey>('created_at');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [exportError, setExportError] = useState<string | null>(null);
  const { data: summary, isLoading, isError } = useGetCommunityAnalyticsProjectSummaryQuery(projectId ?? '', {
    skip: !projectId,
  });
  const { data: sessionsData, isLoading: isLoadingSessions, isFetching: isFetchingSessions, isError: isSessionsError } =
    useGetCommunityAnalyticsProjectSessionsQuery(
      {
        projectId: projectId ?? '',
        page,
        limit: PAGE_SIZE,
        column_key: columnKey,
        order,
        created_from: startDate ? formatDateBoundary(startDate, false) : undefined,
        created_to: endDate ? formatDateBoundary(endDate, true) : undefined,
        statuses: selectedStatus ? [selectedStatus] : [],
      },
      { skip: !projectId },
    );
  const [getSessionsReport, { isFetching: isExporting }] = useLazyGetCommunityAnalyticsProjectSessionsReportQuery();

  if (!projectId) {
    return <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-red-700">Не передан идентификатор сценария.</div>;
  }

  if (isLoading) {
    return <div className="rounded-lg bg-white p-8 text-center text-gray-500 shadow-md">Загружаем сценарий...</div>;
  }

  if (isError || !summary) {
    return (
      <div className="space-y-4">
        <button type="button" onClick={() => navigate('/community-analytics')} className="cursor-pointer text-sm font-medium text-blue-600 hover:text-blue-700">
          ← К аналитике сообщества
        </button>
        <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-red-700">Не удалось загрузить сценарий.</div>
      </div>
    );
  }

  const metrics = [
    { label: 'Saves', value: summary.saves_count },
    { label: 'Sessions', value: summary.session_count },
    { label: 'Participants', value: summary.participant_count },
    { label: 'Completion rate', value: `${summary.completion_rate}%` },
    { label: 'Pass rate', value: `${summary.success_rate}%` },
    { label: 'Score', value: summary.score },
  ];
  const sessions = sessionsData?.documents ?? [];
  const total = sessionsData?.total ?? 0;
  const currentPage = sessionsData?.page ?? page;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const fromItem = total === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const toItem = Math.min(currentPage * PAGE_SIZE, total);

  const toggleSort = (nextColumnKey: SessionSortKey) => {
    setPage(1);
    if (nextColumnKey === columnKey) {
      setOrder((currentOrder) => (currentOrder === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setColumnKey(nextColumnKey);
    setOrder('asc');
  };

  const downloadSessions = async () => {
    try {
      setExportError(null);
      const report = await getSessionsReport({
        projectId,
        column_key: columnKey,
        order,
        created_from: startDate ? formatDateBoundary(startDate, false) : undefined,
        created_to: endDate ? formatDateBoundary(endDate, true) : undefined,
        statuses: selectedStatus ? [selectedStatus] : [],
        locale: navigator.language || 'en',
        time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      }).unwrap();
      const url = URL.createObjectURL(report);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'community-scenario-sessions.xlsx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch {
      setExportError('Failed to download sessions report.');
    }
  };

  return (
    <div className="space-y-6">
      <button type="button" onClick={() => navigate('/community-analytics')} className="cursor-pointer text-sm font-medium text-blue-600 hover:text-blue-700">
        ← К аналитике сообщества
      </button>

      <section className="rounded-2xl bg-white p-8 shadow-md">
        <div className="flex flex-col gap-6 md:flex-row">
          <img src={summary.avatar_url} alt="" className="h-32 w-32 shrink-0 rounded-xl bg-gray-100 object-cover" />
          <div className="min-w-0 flex-1">
            <h1 className="text-3xl font-semibold text-gray-900">{summary.title}</h1>
            {summary.description && <p className="mt-3 whitespace-pre-wrap text-sm leading-6 text-gray-600">{summary.description}</p>}
            <div className="mt-5">
              <div className="text-xs font-medium tracking-wide text-gray-500 uppercase">Author</div>
              <div className="mt-1 text-sm font-medium text-gray-900">{getAuthorName(summary.author.first_name, summary.author.last_name, summary.author.email)}</div>
              <div className="text-sm text-gray-500">{summary.author.email}</div>
            </div>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {metrics.map((metric) => (
            <div key={metric.label} className="rounded-xl bg-slate-50 p-4">
              <div className="text-xs tracking-wide text-gray-500 uppercase">{metric.label}</div>
              <div className="mt-2 text-2xl font-semibold text-gray-900">{metric.value}</div>
            </div>
          ))}
        </div>
      </section>

      <section>
        <div className="mb-4 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Sessions</h2>
            <p className="mt-1 text-sm text-gray-600">Sessions completed in organizations that copied this scenario.</p>
          </div>
          <button type="button" onClick={() => void downloadSessions()} disabled={isExporting} className="cursor-pointer rounded-md bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-emerald-300">
            {isExporting ? 'Preparing XLSX...' : 'Download XLSX'}
          </button>
        </div>

        {exportError && <p className="mb-4 text-sm text-red-600">{exportError}</p>}

        <div className="mb-4 grid max-w-3xl gap-4 md:grid-cols-3">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="community-session-start-date">From</label>
            <input id="community-session-start-date" type="date" value={startDate} onChange={(event) => { setStartDate(event.target.value); setPage(1); }} className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500" />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="community-session-end-date">To</label>
            <input id="community-session-end-date" type="date" value={endDate} onChange={(event) => { setEndDate(event.target.value); setPage(1); }} className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500" />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="community-session-status">Status</label>
            <select id="community-session-status" value={selectedStatus} onChange={(event) => { setSelectedStatus(event.target.value as CommunityAnalyticsSessionStatus | ''); setPage(1); }} className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500">
              <option value="">All statuses</option>
              <option value="COMPLETED_WITH_REPORT">Completed with report</option>
              <option value="ENGAGED_NO_REPORT">Engaged, no report</option>
            </select>
          </div>
        </div>

        <div className="overflow-hidden rounded-lg bg-white shadow-md">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {columns.map((column) => (
                    <th key={column.key} scope="col" className={`px-6 py-3 text-xs font-medium tracking-wider text-gray-500 uppercase whitespace-nowrap ${column.align === 'right' ? 'text-right' : 'text-left'}`}>
                      <button type="button" onClick={() => toggleSort(column.key)} className={`flex w-full cursor-pointer items-center gap-1 hover:text-gray-900 ${column.align === 'right' ? 'justify-end' : 'justify-start'}`}>
                        {column.label}
                        <span aria-hidden="true">{column.key === columnKey ? (order === 'asc' ? '↑' : '↓') : '↕'}</span>
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {isLoadingSessions && <tr><td colSpan={columns.length} className="px-6 py-10 text-center text-sm text-gray-500">Loading sessions...</td></tr>}
                {isSessionsError && <tr><td colSpan={columns.length} className="px-6 py-10 text-center text-sm text-red-600">Failed to load sessions.</td></tr>}
                {!isLoadingSessions && !isSessionsError && sessions.length === 0 && <tr><td colSpan={columns.length} className="px-6 py-10 text-center text-sm text-gray-500">No sessions found.</td></tr>}
                {sessions.map((session) => (
                  <tr key={session.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-900 whitespace-nowrap">{formatDateTime(session.created_at)}</td>
                    <td className="px-6 py-4 text-sm text-gray-900 whitespace-nowrap">{session.organization_name}</td>
                    <td className="px-6 py-4 text-sm text-gray-900 whitespace-nowrap">{getParticipantName(session)}</td>
                    <td className="px-6 py-4 text-sm text-gray-900 text-right">{formatDuration(session.duration_seconds)}</td>
                    <td className="px-6 py-4 text-sm text-gray-900 text-right">{session.score}</td>
                    <td className="px-6 py-4 text-sm text-gray-900 whitespace-nowrap">{formatStatus(session.status)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-4">
          <span className="text-sm text-gray-600">{isFetchingSessions ? 'Updating...' : `Showing ${fromItem}-${toItem} of ${total}`}</span>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-600">Page {currentPage} of {totalPages}</span>
            <button type="button" onClick={() => setPage((currentPageValue) => Math.max(1, currentPageValue - 1))} disabled={currentPage <= 1 || isFetchingSessions} className="cursor-pointer rounded-md bg-gray-600 px-4 py-2 text-sm font-medium text-white hover:bg-gray-700 disabled:cursor-not-allowed disabled:bg-gray-300">Previous</button>
            <button type="button" onClick={() => setPage((currentPageValue) => Math.min(totalPages, currentPageValue + 1))} disabled={currentPage >= totalPages || isFetchingSessions} className="cursor-pointer rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-300">Next</button>
          </div>
        </div>
      </section>
    </div>
  );
};
